import { Link } from "react-router-dom";
import { publications } from "../data/publications";
import { phdResearchProjects } from "../data/phdResearchProjects";
import { researchPillars } from "../data/researchPillars";
import { collaborators } from "../data/collaborators";
import { mentoring } from "../data/mentoring";
import { newsItems } from "../data/news";
import { features } from "../data/features";
import { isProjectShown } from "../utils/projects";

const COLLECTIONS = {
  phdResearchProjects: phdResearchProjects.filter(isProjectShown),
  researchPillars,
  collaborators,
  mentoring,
  publications,
  newsItems,
  features,
};

const labelFor = (collection, id) => {
  const list = COLLECTIONS[collection] || [];
  const item = list.find((entry) => {
    if (typeof entry === "string") return entry === id;
    return entry.id === id || entry.title === id;
  });
  if (!item) return id;
  if (typeof item === "string") return item;
  const raw =
    item.bigGoal ||
    item.firstname ||
    item.title ||
    item.name ||
    id;
  let label = String(raw).replace(/<[^>]+>/g, "").trim();
  if (item.date && (collection === "newsItems" || collection === "features")) {
    label = `${item.date}: ${label}`;
  }
  if (label.length > 72) label = `${label.slice(0, 69)}…`;
  return label;
};

const hrefFor = (typeId, id) => {
  if (typeId === "projects") return `/home?view=projects&project=${id}`;
  if (typeId === "pillars") return `/home?view=agenda&pillar=${id}`;
  return null;
};

/**
 * Renders item.associations on public pages.
 * Unknown types still show as chips/links/text.
 */
const AssociationsDisplay = ({
  associations = {},
  associationTypes = [],
  excludeTypeIds = [],
  className = "",
}) => {
  const skip = new Set(excludeTypeIds);
  const entries = Object.entries(associations || {}).filter(([typeId, value]) => {
    if (skip.has(typeId)) return false;
    if (value == null || value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
  if (!entries.length) return null;

  const typeById = Object.fromEntries(
    (associationTypes || []).map((t) => [t.id, t]),
  );

  return (
    <div className={`space-y-3 ${className}`}>
      {entries.map(([typeId, value]) => {
        const type = typeById[typeId] || {
          id: typeId,
          label: typeId,
          kind: Array.isArray(value)
            ? value[0]?.url !== undefined
              ? "urls"
              : "collection"
            : "text",
        };

        return (
          <div key={typeId}>
            <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              {type.label || typeId}
            </p>
            {type.kind === "text" || typeof value === "string" ? (
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {String(value)}
              </p>
            ) : type.kind === "urls" ? (
              <ul className="mt-1 space-y-1 text-sm">
                {(value || []).map((link, idx) => (
                  <li key={idx}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green dark:text-green-dark underline underline-offset-2"
                    >
                      {link.label || link.url}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(value || []).map((id) => {
                  const href = hrefFor(typeId, id);
                  const label = labelFor(type.collection, id);
                  const chipClass =
                    "rounded-full border border-gray-300 px-2 py-0.5 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-400";
                  if (href) {
                    return (
                      <Link key={id} to={href} className={`${chipClass} hover:border-green hover:text-green dark:hover:text-green-dark`}>
                        {label}
                      </Link>
                    );
                  }
                  return (
                    <span key={id} className={chipClass}>
                      {label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssociationsDisplay;
